import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { Mail, Lock, User, Phone, Building, ArrowRight, Eye, EyeOff, CheckCircle, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useReCaptchaConfig } from '@/components/ReCaptchaProvider';
import axios from 'axios';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_2452ebe5-8b88-4541-a707-a9ae800f68db/artifacts/oj0fg93b_ChatGPT%20Image%2011%20mrt%202026%2C%2015_21_20.png";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RegisterPage = () => {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [registrationComplete, setRegistrationComplete] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    company: '',
    password: '',
    confirm_password: ''
  });

  // reCAPTCHA hooks
  const { enabled: recaptchaEnabled } = useReCaptchaConfig();
  const { executeRecaptcha } = useGoogleReCaptcha() || {};

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.first_name || !formData.last_name || !formData.email || !formData.password) {
      toast.error(t('register.fillRequired'));
      return;
    }

    if (formData.password !== formData.confirm_password) {
      toast.error(t('register.passwordsMismatch'));
      return;
    }

    if (formData.password.length < 8) {
      toast.error(t('register.passwordMinLength'));
      return;
    }

    setLoading(true);
    try {
      const { confirm_password, ...userData } = formData;
      
      // Get reCAPTCHA token if enabled
      let recaptchaToken = null;
      if (recaptchaEnabled && executeRecaptcha) {
        try {
          recaptchaToken = await executeRecaptcha('register');
        } catch (recaptchaError) {
          console.error('reCAPTCHA error:', recaptchaError);
          // Continue without token - backend will handle
        }
      }
      
      await axios.post(`${API}/auth/register`, {
        ...userData,
        recaptcha_token: recaptchaToken
      });
      setRegisteredEmail(formData.email);
      setRegistrationComplete(true);
      toast.success(i18n.language === 'nl' 
        ? 'Registratie succesvol! Controleer uw e-mail.' 
        : 'Registration successful! Please check your email.');
    } catch (error) {
      console.error('Registration failed:', error);
      if (error.response?.status === 429) {
        toast.error(i18n.language === 'nl' 
          ? 'Te veel verzoeken. Probeer het later opnieuw.' 
          : 'Too many requests. Please try again later.');
      } else if (error.response?.data?.detail?.includes('CAPTCHA')) {
        toast.error(i18n.language === 'nl' 
          ? 'Beveiligingsverificatie mislukt. Probeer het opnieuw.' 
          : 'Security verification failed. Please try again.');
      } else {
        toast.error(error.response?.data?.detail || t('register.error'));
      }
    } finally {
      setLoading(false);
    }
  };

  // Show verification pending screen after successful registration
  if (registrationComplete) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-navy-800 relative overflow-hidden py-20" data-testid="registration-complete">
        <div className="absolute inset-0 bg-grid-pattern bg-grid opacity-10" />
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-md mx-auto"
          >
            <div className="text-center mb-8">
              <Link to="/">
                <img src={LOGO_URL} alt="Elementary Cargo" className="h-20 mx-auto mb-4" />
              </Link>
            </div>
            
            <Card className="shadow-2xl border-0">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-green-600" aria-hidden="true" />
                </div>
                <h2 className="text-xl font-bold text-navy-800 mb-2">
                  {i18n.language === 'nl' ? 'Registratie Succesvol!' : 'Registration Successful!'}
                </h2>
                <p className="text-steel-600 mb-4">
                  {i18n.language === 'nl' 
                    ? 'We hebben een verificatie e-mail gestuurd naar:' 
                    : 'We have sent a verification email to:'}
                </p>
                <p className="text-navy-800 font-semibold mb-6">{registeredEmail}</p>
                <div className="bg-gold/10 rounded-lg p-4 mb-6">
                  <p className="text-sm text-navy-700">
                    {i18n.language === 'nl' 
                      ? 'Klik op de link in de e-mail om uw account te activeren. De link is 24 uur geldig.' 
                      : 'Click the link in the email to activate your account. The link is valid for 24 hours.'}
                  </p>
                </div>
                <div className="space-y-3">
                  <Link to="/login">
                    <Button className="w-full bg-gold hover:bg-gold/90 text-navy-900">
                      {i18n.language === 'nl' ? 'Ga naar Inloggen' : 'Go to Login'}
                    </Button>
                  </Link>
                  <p className="text-sm text-steel-500">
                    {i18n.language === 'nl' 
                      ? 'Geen e-mail ontvangen? Controleer uw spam-folder of' 
                      : "Didn't receive the email? Check your spam folder or"}
                  </p>
                  <Link to="/resend-verification" className="text-gold hover:text-gold/80 text-sm font-medium">
                    {i18n.language === 'nl' ? 'verstuur opnieuw' : 'resend verification'}
                  </Link>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-navy-800 relative overflow-hidden py-20" data-testid="register-page">
      {/* Background */}
      <div className="absolute inset-0 bg-grid-pattern bg-grid opacity-10" />
      
      {/* Content */}
      <div className="container-custom relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md mx-auto"
        >
          {/* Logo */}
          <div className="text-center mb-8">
            <Link to="/">
              <img 
                src={LOGO_URL} 
                alt="Elementary Cargo" 
                className="h-20 mx-auto mb-4"
              />
            </Link>
            <h1 className="font-outfit text-2xl font-bold text-white mb-2">{t('register.title')}</h1>
            <p className="text-steel-400">{t('register.subtitle')}</p>
          </div>

          <Card className="shadow-2xl border-0">
            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-5" data-testid="register-form">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-navy-800">{t('register.firstName')} *</Label>
                    <div className="relative mt-2">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-steel-400" />
                      <Input
                        value={formData.first_name}
                        onChange={(e) => handleChange('first_name', e.target.value)}
                        placeholder={t('register.firstName')}
                        className="pl-10"
                        data-testid="register-first-name"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-navy-800">{t('register.lastName')} *</Label>
                    <div className="relative mt-2">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-steel-400" />
                      <Input
                        value={formData.last_name}
                        onChange={(e) => handleChange('last_name', e.target.value)}
                        placeholder={t('register.lastName')}
                        className="pl-10"
                        data-testid="register-last-name"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <Label className="text-navy-800">{t('register.email')} *</Label>
                  <div className="relative mt-2">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-steel-400" />
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      placeholder={t('login.emailPlaceholder')}
                      className="pl-10"
                      data-testid="register-email"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-navy-800">{t('register.phone')}</Label>
                  <div className="relative mt-2">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-steel-400" />
                    <Input
                      value={formData.phone}
                      onChange={(e) => handleChange('phone', e.target.value)}
                      placeholder="+1 (234) 567-890"
                      className="pl-10"
                      data-testid="register-phone"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-navy-800">{t('register.company')}</Label>
                  <div className="relative mt-2">
                    <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-steel-400" />
                    <Input
                      value={formData.company}
                      onChange={(e) => handleChange('company', e.target.value)}
                      placeholder={t('register.companyPlaceholder')}
                      className="pl-10"
                      data-testid="register-company"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-navy-800">{t('register.password')} *</Label>
                  <div className="relative mt-2">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-steel-400" />
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={(e) => handleChange('password', e.target.value)}
                      placeholder={t('register.passwordPlaceholder')}
                      className="pl-10 pr-10"
                      data-testid="register-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-steel-400 hover:text-steel-600"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div>
                  <Label className="text-navy-800">{t('register.confirmPassword')} *</Label>
                  <div className="relative mt-2">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-steel-400" />
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      value={formData.confirm_password}
                      onChange={(e) => handleChange('confirm_password', e.target.value)}
                      placeholder={t('register.confirmPasswordPlaceholder')}
                      className="pl-10"
                      data-testid="register-confirm-password"
                    />
                  </div>
                </div>

                <div className="flex items-start gap-2">
                  <input type="checkbox" className="rounded border-steel-300 mt-1" required />
                  <span className="text-sm text-steel-600">
                    {t('register.agreeTerms')}{' '}
                    <Link to="/terms" className="text-gold hover:underline">{t('register.termsOfService')}</Link>
                    {' '}{t('register.and')}{' '}
                    <Link to="/privacy" className="text-gold hover:underline">{t('register.privacyPolicy')}</Link>
                  </span>
                </div>

                <Button 
                  type="submit" 
                  className="btn-accent w-full" 
                  disabled={loading}
                  data-testid="register-submit-btn"
                >
                  {loading ? t('register.creatingAccount') : t('register.createAccount')}
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>

                {/* reCAPTCHA badge notice */}
                {recaptchaEnabled && (
                  <div className="flex items-center justify-center gap-2 text-xs text-steel-500 mt-4">
                    <Shield className="w-3 h-3 text-green-500" />
                    <span>
                      {i18n.language === 'nl' 
                        ? 'Beschermd door reCAPTCHA' 
                        : 'Protected by reCAPTCHA'}
                    </span>
                  </div>
                )}
              </form>

              <div className="mt-6 text-center">
                <p className="text-steel-600 text-sm">
                  {t('register.haveAccount')}{' '}
                  <Link to="/login" className="text-gold font-medium hover:underline" data-testid="login-link">
                    {t('register.signIn')}
                  </Link>
                </p>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default RegisterPage;
